import { makeApiRequestPromise } from "./api";

/**
 * Client for the global-api service — a distinct service from the regional eval
 * API (usage/billing today, alerting later). It has its own base URL and its own
 * required env var, so it can evolve independently of the eval API.
 *
 * The transport (Cognito bearer token + X-Impersonate-Domain) is shared with the
 * eval API — both hit the same Cognito pool — so we reuse `makeApiRequestPromise`
 * rather than reimplement auth here; only the base URL differs.
 */

interface GlobalApiRequestOptions {
  path: string;
  method: string;
  body?: any;
  params?: Record<string, string | number | string[] | undefined>;
}

/**
 * Resolve the global-api base URL. There is deliberately NO fallback to the eval
 * API: each environment must configure `VITE_GLOBAL_API_URL` explicitly, so a
 * misconfiguration fails loudly instead of silently routing global-api traffic
 * to a different service. Isolated in this function so the `import.meta.env` read
 * is easy to mock in jest (which can't evaluate `import.meta` under CommonJS).
 */
export const globalApiBaseUrl = (): string => {
  const url = import.meta.env.VITE_GLOBAL_API_URL as string | undefined;
  if (!url) {
    throw new Error(
      "VITE_GLOBAL_API_URL is not configured — the global-api base URL must be set explicitly for this environment."
    );
  }
  return url;
};

/** Make a request against the global-api service. */
export const makeGlobalApiRequest = ({
  path,
  method,
  body,
  params,
}: GlobalApiRequestOptions): Promise<any> =>
  makeApiRequestPromise({
    path,
    method,
    body,
    params,
    baseUrl: globalApiBaseUrl(),
  });
